import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/hooks';
import { BookingForm, toAdditionalServiceUuids } from '@/features/bookings';
import type { BookingFormSubmitData } from '@/features/bookings';
import { normalizePhoneToE164, resolveCanAdminUpdateClientName } from '@/features/clients';
import { getApiErrorMessage } from '@/utils/errors';
import {
  adminFindOrCreateClient,
  adminCreateOrUpdateCar,
  adminCreateBooking,
} from '@pro-sto/api-client';

interface CreateBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  initialDate?: Date;
  initialTime?: string;
  initialPostUuid?: string;
}

const CreateBookingModal = observer(({
  isOpen,
  onClose,
  onSuccess,
  initialDate,
  initialTime,
  initialPostUuid,
}: CreateBookingModalProps) => {
  const { toastStore, authStore, bookingsStore, servicesStore } = useStores();

  const handleSubmit = useCallback(async (data: BookingFormSubmitData) => {
    if (!authStore.user?.service_center_uuid) {
      toastStore.showError('Не найден UUID сервисного центра');
      return;
    }

    try {
      const cleanPhone = normalizePhoneToE164(data.phone);
      if (!cleanPhone) {
        toastStore.showError('Неверный формат телефона');
        return;
      }

      const trimmedName = data.clientName.trim();
      const knownClientUuid = data.selectedClient?.uuid;
      const canUpdateName = await resolveCanAdminUpdateClientName({
        clientUuid: knownClientUuid,
        serviceCenterUuid: authStore.user.service_center_uuid,
        localBookings: [...bookingsStore.bookings, ...bookingsStore.pendingBookings],
      });

      const clientResponse = await adminFindOrCreateClient({
        requestBody: {
          phone: cleanPhone,
          ...(trimmedName && canUpdateName ? { name: trimmedName } : {}),
        },
      });
      const clientUuid = clientResponse.uuid;

      // 2. Найти или создать автомобиль
      let carUuid: string;

      if (data.selectedCar && !data.carDetailsModified) {
        carUuid = data.selectedCar.uuid;
      } else {
        // Если данные автомобиля не указаны, используем дефолтные значения
        const carLicensePlate = data.licensePlate || 'Не указан';
        const carMake = data.selectedMake?.label || 'Не указана';
        const carModel = data.selectedModel?.label || 'Не указана';
        const carMakeId = data.selectedMake?.value ? String(data.selectedMake.value) : undefined;
        const carModelId = data.selectedModel?.value ? String(data.selectedModel.value) : undefined;

        const carResponse = await adminCreateOrUpdateCar({
          clientUuid,
          requestBody: {
            license_plate: carLicensePlate,
            make: carMake,
            model: carModel,
            make_id: carMakeId,
            model_id: carModelId,
          },
        });
        carUuid = carResponse.uuid;
      }

      // 3. Сформировать время начала
      const [hours, minutes] = data.selectedTime.split(':').map(Number);
      const startTime = new Date(data.selectedDate!);
      startTime.setHours(hours, minutes, 0, 0);

      // 4. Создать бронирование
      const additionalServiceUuids = toAdditionalServiceUuids(
        data.selectedAdditionalServices,
        servicesStore.additionalServices,
      );

      const created = await adminCreateBooking({
        requestBody: {
          service_center_uuid: authStore.user.service_center_uuid,
          client_uuid: clientUuid,
          car_uuid: carUuid,
          service_uuid: String(data.selectedService!.value),
          post_uuid: String(data.selectedPost!.value),
          start_time: startTime.toISOString(),
          payment_method: 'cash',
          additional_service_uuids: additionalServiceUuids,
          admin_comment: data.comment || undefined,
        },
      });

      toastStore.showSuccess('Запись успешно создана');

      if (trimmedName && canUpdateName) {
        bookingsStore.setClientDisplayName(clientUuid, trimmedName);
      }

      await Promise.resolve(onSuccess());

      if (trimmedName && canUpdateName && created.uuid) {
        bookingsStore.applyClientNameToBooking(created.uuid, trimmedName);
      }
      onClose();
    } catch (error: unknown) {
      console.error('Failed to create booking:', error);

      const apiError = error as { status?: number };
      if (apiError?.status === 409) {
        toastStore.showError('Временной слот уже занят');
      } else if (apiError?.status === 404) {
        toastStore.showError(getApiErrorMessage(error, 'Не найдены необходимые данные'));
      } else if (apiError?.status === 400) {
        toastStore.showError(getApiErrorMessage(error, 'Некорректные данные запроса'));
      } else {
        toastStore.showError('Не удалось создать запись');
      }
      throw error;
    }
  }, [authStore.user, bookingsStore, servicesStore, toastStore, onSuccess, onClose]);

  return (
    <BookingForm
      isOpen={isOpen}
      onClose={onClose}
      mode="create"
      title="Добавление записи"
      submitLabel="Добавить"
      onSubmit={handleSubmit}
      initialDate={initialDate}
      initialTime={initialTime}
      initialPostUuid={initialPostUuid}
    />
  );
});

export default CreateBookingModal;
